import moment from 'moment-timezone';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { currentUser as currentUserSelector } from 'src/redux/auth.slice';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import { createTrackView, deleteTrackView, getTrackViewList, updateTrackView } from 'src/services/RadarService';
import WalToast from 'src/utils/WalToast';

import { ITrackFilterForm } from '../modals/TrackFilterModal';
import { MAX_TRACK_VIEW_COUNT, TRACK_DEFAULT_TAB_VALUES, TRACK_TABLE_ALIAS } from '../track.const';
import { buildAppliedTrackView, extractTrackViewNames, formatTrackTabData, isTrackViewChanged, makeAppliedTrackViewParamsPatch, makeDefaultTrackViewParamsPatch } from '../track.helpers';
import { ITrackParams, ITrackTab } from '../track.interface';
import { readTrackActiveView, readTrackFilterState, writeTrackActiveView, writeTrackFilterState } from '../track.preferences';
import {
    setTrackActiveViewTab,
    setTrackIsFetchedTabs,
    setTrackLoading,
    setTrackParams,
    setTrackTabsData,
    trackActiveViewTab,
    trackCount as trackCountSelector,
    trackIsFetchedTabs,
    trackParams as trackParamsSelector,
    trackTabsData,
} from '../track.slice';

// ── Saved views for the Track page ────────────────────────────────────────────
// ONE state machine for both surfaces: the table view's tab row (Pickups / In Transit /
// Deliveries + saved views, with Rename / Edit / Delete and Cancel / Save View) and the
// map view's Overview card (view dropdown + Save View). Views live in custom_views
// (tableAlias 'cp_track') through the portal's generic /view/* API; the tab list and the
// active tab live in the track slice so both surfaces stay in sync. Ported from WAL's
// Radar TableView.tsx (lines 331-694) and MapLeftSidebarViewNew.tsx.
//
// Vocabulary: `activeViewTab` is a stage value while a default tab is active, or the
// saved view's id (as string) while a saved view is active. `isIconShow` on a tab is the
// only marker that says "this is a saved view".

export interface ITrackViewAction {
    create_view: boolean;
    rename_view: boolean;
    delete_view: boolean;
    data: any;
}

const INIT_VIEW_ACTION: ITrackViewAction = { create_view: false, rename_view: false, delete_view: false, data: null };

// Shared in-flight request so two consumers mounting together (table + map surfaces)
// issue ONE list call instead of racing each other.
let viewListRequest: Promise<any> | null = null;

export const useTrackViews = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const trackParams = useAppSelector(trackParamsSelector);
    const trackCount = useAppSelector(trackCountSelector);
    // Slice holds ONLY saved views; the stage tabs are derived here with live counts.
    const customViews = useAppSelector(trackTabsData);
    const activeViewTab = useAppSelector(trackActiveViewTab);
    const isFetchedTabs = useAppSelector(trackIsFetchedTabs);
    const currentUser = useAppSelector(currentUserSelector);
    const userId: number | string | undefined = currentUser?.id;
    // Timezone lives on basic.appearance (the reader's clock) — "today" is the Track
    // default date range, which reads as "no date filter".
    const userTimezone = useAppSelector((state) => state.basic.appearance?.timezone) || 'UTC';
    const getToday = useCallback(() => moment().tz(userTimezone).format('YYYY-MM-DD'), [userTimezone]);

    const [viewAction, setViewAction] = useState<ITrackViewAction>(INIT_VIEW_ACTION);
    const [isViewChangeLoading, setIsViewChangeLoading] = useState(false);
    // 422 from the API = duplicate name; CreateViewModal renders it inline under the field.
    const [isError, setIsError] = useState(false);
    const [isTabsLoading, setIsTabsLoading] = useState(!isFetchedTabs);
    // Guards the filter-state WRITE until the RESTORE below has run. Without it the write effect
    // fires on mount with the default params and overwrites the stored filters a moment before
    // fetchViews gets to read them — which is why restoring appeared to do nothing.
    const hasRestoredFiltersRef = useRef(false);

    const defaultTabs = useMemo<ITrackTab[]>(
        () => [
            { name: t('Radar.Pickups'), value: 'dispatched', count: trackCount.pickups.total },
            { name: t('Radar.In_Transit'), value: 'in-transit', count: trackCount.inTransit.total },
            { name: t('Radar.Deliveries'), value: 'delivered', count: trackCount.deliveries.total },
        ],
        [trackCount, t]
    );

    const tabs = useMemo<ITrackTab[]>(() => [...defaultTabs, ...customViews], [defaultTabs, customViews]);
    const viewNames = useMemo(() => extractTrackViewNames(customViews), [customViews]);
    const isDefaultTab = TRACK_DEFAULT_TAB_VALUES.includes(activeViewTab);
    const activeCustomView = useMemo(() => customViews.find((view) => view.value === activeViewTab), [customViews, activeViewTab]);
    const isCreateViewEnable = tabs.length < MAX_TRACK_VIEW_COUNT;

    // Baseline the current params are compared against: the active saved view, or (on a
    // stage tab) the clean defaults for that stage with today's date.
    const baselineView = useMemo<Partial<ITrackTab>>(
        () => activeCustomView ?? buildAppliedTrackView(makeDefaultTrackViewParamsPatch(trackParams.status, getToday())),
        [activeCustomView, trackParams.status, getToday]
    );
    const isSaveEnable = useMemo(() => isTrackViewChanged(baselineView, trackParams), [baselineView, trackParams]);

    // While a stage tab is active the tab must follow the stage — the filter modal's Stage
    // select and the overview card both change `status` without going through a tab click.
    // A saved view keeps its highlight regardless (changing the stage just makes it dirty).
    useEffect(() => {
        if (isDefaultTab && activeViewTab !== trackParams.status && TRACK_DEFAULT_TAB_VALUES.includes(trackParams.status)) {
            dispatch(setTrackActiveViewTab(trackParams.status));
        }
    }, [dispatch, isDefaultTab, activeViewTab, trackParams.status]);

    // ── Apply ────────────────────────────────────────────────────────────────
    const applyView = useCallback(
        (view: ITrackTab) => {
            dispatch(setTrackLoading(true));
            dispatch(setTrackParams(makeAppliedTrackViewParamsPatch(view.appliedFilters, view.appliedSorting, getToday(), trackParams.status)));
            dispatch(setTrackActiveViewTab(view.value));
            writeTrackActiveView(userId, view.value);
        },
        [dispatch, getToday, trackParams.status, userId]
    );

    // Stage tab click. Coming FROM a saved view resets every field (the view's filters must
    // not leak into the stage); stage → stage only swaps the stage, keeping the user's
    // ad-hoc filters — same as WAL.
    const applyDefaultTab = useCallback(
        (stage: string, options: { fullReset?: boolean } = {}) => {
            dispatch(setTrackLoading(true));

            if (options.fullReset || activeCustomView) {
                dispatch(setTrackParams(makeDefaultTrackViewParamsPatch(stage, getToday())));
            } else {
                dispatch(setTrackParams({ status: stage, filterBySubStatus: '', page: 1 }));
            }

            dispatch(setTrackActiveViewTab(stage));
            writeTrackActiveView(userId, null);
        },
        [dispatch, getToday, activeCustomView, userId]
    );

    // Cancel in the filter bar: throw away unsaved changes and go back to the baseline.
    // `stage` lets the caller restore the stage that was active when the bar opened (the
    // Stage select may have moved it in the meantime).
    const revertToBaseline = useCallback(
        (stage?: string) => {
            if (!isSaveEnable) return;

            if (activeCustomView) {
                dispatch(setTrackParams(makeAppliedTrackViewParamsPatch(activeCustomView.appliedFilters, activeCustomView.appliedSorting, getToday(), trackParams.status)));
            } else {
                const targetStage = stage && TRACK_DEFAULT_TAB_VALUES.includes(stage) ? stage : trackParams.status;
                dispatch(setTrackParams(makeDefaultTrackViewParamsPatch(targetStage, getToday())));
                dispatch(setTrackActiveViewTab(targetStage));
            }
        },
        [dispatch, isSaveEnable, activeCustomView, getToday, trackParams.status]
    );

    // Filter-modal Apply (both surfaces). Dates come back as Date objects from DateSelect;
    // a missing side keeps the current one, and an inverted range is collapsed onto the
    // side the user actually touched.
    const applyModalFilters = useCallback(
        (data: ITrackFilterForm) => {
            let startDate = data.startDate ? moment.utc(data.startDate).format('YYYY-MM-DD') : trackParams.startDate;
            let endDate = data.endDate ? moment.utc(data.endDate).format('YYYY-MM-DD') : trackParams.endDate;

            if (startDate && endDate && moment(startDate).isAfter(moment(endDate))) {
                if (data.startDate) {
                    endDate = startDate;
                } else {
                    startDate = endDate;
                }
            }

            dispatch(
                setTrackParams({
                    ...data,
                    status: data.status ?? trackParams.status,
                    serviceType: data.serviceType ?? 'all',
                    shipperId: data.shipperId ?? null,
                    consigneeId: data.consigneeId ?? null,
                    startDate,
                    endDate,
                    page: 1,
                })
            );
        },
        [dispatch, trackParams.startDate, trackParams.endDate, trackParams.status]
    );

    // ── Fetch (once per session) ─────────────────────────────────────────────
    const fetchViews = useCallback(async () => {
        setIsTabsLoading(true);

        try {
            if (!viewListRequest) viewListRequest = getTrackViewList({ tableAlias: TRACK_TABLE_ALIAS });

            const response: any = await viewListRequest;
            const rows = response?.data;
            const views = formatTrackTabData(Array.isArray(rows) ? rows : []);

            dispatch(setTrackTabsData(views));
            dispatch(setTrackIsFetchedTabs(true));

            // Land on the view the user had open last time; a view deleted elsewhere just
            // clears the memory and leaves the default stage.
            const remembered = readTrackActiveView(userId);

            const rememberedView = remembered ? views.find((item) => item.value === remembered) : null;

            if (rememberedView) {
                dispatch(setTrackParams(makeAppliedTrackViewParamsPatch(rememberedView.appliedFilters, rememberedView.appliedSorting, getToday(), trackParams.status)));
                dispatch(setTrackActiveViewTab(rememberedView.value));
            } else {
                // A view deleted elsewhere just clears the memory and leaves the default stage.
                if (remembered) writeTrackActiveView(userId, null);

                // No saved view active — restore the ad-hoc filters the user last had applied, so a
                // refresh doesn't drop them back to Pickups/Today. One patch, so it costs a single
                // refetch (and none at all when it matches what's already in state).
                const storedFilters = readTrackFilterState(userId);

                if (storedFilters) {
                    dispatch(setTrackParams(storedFilters.params));

                    if (storedFilters.activeViewTab && TRACK_DEFAULT_TAB_VALUES.includes(storedFilters.activeViewTab)) {
                        dispatch(setTrackActiveViewTab(storedFilters.activeViewTab));
                    }
                }
            }
        } catch (err: any) {
            console.error('useTrackViews: failed to load saved views', err);
            WalToast.error(err?.response?.data?.message || t('Common.Something_went_wrong'));
        } finally {
            // Restore decided (or failed) — from here on, changes are the user's and get persisted.
            hasRestoredFiltersRef.current = true;
            viewListRequest = null;
            setIsTabsLoading(false);
        }
    }, [dispatch, userId, getToday, trackParams.status, t]);

    // Mirror the working filter set into session storage on every change — the restore above
    // reads it on the next load. Cheap enough to run unconditionally; the write is one small
    // JSON blob and never touches the network.
    useEffect(() => {
        if (!userId || !hasRestoredFiltersRef.current) return;

        writeTrackFilterState(userId, trackParams as unknown as Record<string, unknown>, activeViewTab ?? null);
    }, [userId, trackParams, activeViewTab]);

    useEffect(() => {
        if (!userId) return;

        if (!isFetchedTabs) {
            void fetchViews();

            return;
        }

        // Views already loaded this session — e.g. a second instance of this hook (the map
        // sidebar and the table share it), or coming back from a detail page. There is nothing
        // to restore, so open the write gate straight away or this instance would never persist.
        hasRestoredFiltersRef.current = true;
        // Only the first fetch matters; later param changes must not refetch.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFetchedTabs, userId]);

    // ── Create / rename / update / delete ────────────────────────────────────
    const closeViewAction = useCallback(() => {
        setViewAction(INIT_VIEW_ACTION);
        setIsError(false);
    }, []);

    const openCreateView = useCallback(() => setViewAction({ ...INIT_VIEW_ACTION, create_view: true, data: null }), []);

    const openRenameView = useCallback(() => {
        if (!activeCustomView) return;
        setViewAction({ ...INIT_VIEW_ACTION, rename_view: true, data: { rename: true, tabName: activeCustomView.name, id: activeCustomView.id } });
    }, [activeCustomView]);

    const openDeleteView = useCallback(() => {
        if (!activeCustomView) return;
        setViewAction({ ...INIT_VIEW_ACTION, delete_view: true, data: { view_name: activeCustomView.name } });
    }, [activeCustomView]);

    const handleViewApiError = useCallback(
        (err: any) => {
            if (err?.response?.status === 422) {
                setIsError(true);

                return;
            }

            // e.g. 400 "Maximum number of views (8) reached." — the API message is user-facing
            WalToast.error(err?.response?.data?.message || t('Common.Something_went_wrong'));
        },
        [t]
    );

    /**
     * Create a view and make it active. Saves the CURRENT params unless `params` is given —
     * the filter modal passes its pending (not yet applied) values. Resolves true on success.
     */
    const createView = useCallback(
        async (name: string, params: Partial<ITrackParams> = trackParams): Promise<boolean> => {
            setIsViewChangeLoading(true);

            try {
                const response: any = await createTrackView({ name, tableAlias: TRACK_TABLE_ALIAS, ...buildAppliedTrackView(params) });
                const row = response?.data;

                if (!row?.id) return false;

                const tab = formatTrackTabData([{ ...row, count: response?.count ?? 0 }])[0];
                dispatch(setTrackTabsData([...customViews, tab]));
                dispatch(setTrackActiveViewTab(tab.value));
                writeTrackActiveView(userId, tab.value);
                closeViewAction();

                return true;
            } catch (err: any) {
                handleViewApiError(err);

                return false;
            } finally {
                setIsViewChangeLoading(false);
            }
        },
        [dispatch, trackParams, customViews, userId, closeViewAction, handleViewApiError]
    );

    /**
     * Update a saved view. `rename` changes only the name (filters untouched, params not
     * re-applied); otherwise the CURRENT params — or `options.params`, the filter modal's
     * pending values — overwrite the view's filters + sorting.
     */
    const updateView = useCallback(
        async (view: ITrackTab, options: { name?: string; rename?: boolean; params?: Partial<ITrackParams> } = {}): Promise<boolean> => {
            if (!view.id) return false;

            setIsViewChangeLoading(true);

            try {
                const payload = {
                    name: options.name ?? view.name,
                    tableAlias: TRACK_TABLE_ALIAS,
                    rename: !!options.rename,
                    ...(options.rename ? { appliedFilters: view.appliedFilters, appliedSorting: view.appliedSorting } : buildAppliedTrackView(options.params ?? trackParams)),
                };
                const response: any = await updateTrackView(view.id, payload);
                const row = response?.data;

                if (!row?.id) return false;

                const tab = formatTrackTabData([{ ...row, count: response?.count ?? view.count ?? 0 }])[0];
                dispatch(setTrackTabsData(customViews.map((item) => (item.id === tab.id ? tab : item))));

                // Re-apply so the params match exactly what was saved (dates are normalised
                // server-side) and the dirty check goes clean.
                if (!options.rename) {
                    dispatch(setTrackParams(makeAppliedTrackViewParamsPatch(tab.appliedFilters, tab.appliedSorting, getToday(), trackParams.status)));
                }

                closeViewAction();

                return true;
            } catch (err: any) {
                handleViewApiError(err);

                return false;
            } finally {
                setIsViewChangeLoading(false);
            }
        },
        [dispatch, trackParams, customViews, getToday, closeViewAction, handleViewApiError]
    );

    /** "Save View" while a saved view is active: overwrite it with the current params. */
    const saveActiveView = useCallback(() => (activeCustomView ? updateView(activeCustomView) : Promise.resolve(false)), [activeCustomView, updateView]);

    /** CreateViewModal submit — `modalData.rename` decides between rename and create. */
    const handleViewModalSave = useCallback(
        (data: { name: string; rename?: boolean; id?: number }) => {
            if (data?.rename && activeCustomView) return updateView(activeCustomView, { name: data.name, rename: true });

            return createView(data?.name);
        },
        [activeCustomView, updateView, createView]
    );

    /** DeleteModal close — curried like the component expects: handleClose(status)(). */
    const handleDeleteModalClose = useCallback(
        (status: boolean) => async () => {
            if (!status) {
                closeViewAction();

                return;
            }

            if (!activeCustomView?.id) return;

            setIsViewChangeLoading(true);

            try {
                const response: any = await deleteTrackView(activeCustomView.id);
                const deletedId = response?.data?.id ?? activeCustomView.id;

                dispatch(setTrackTabsData(customViews.filter((item) => item.id !== deletedId)));
                // The deleted view was the active one — land on Pickups with clean filters.
                applyDefaultTab('dispatched', { fullReset: true });
                closeViewAction();
            } catch (err: any) {
                WalToast.error(err?.response?.data?.message || t('Common.Something_went_wrong'));
            } finally {
                setIsViewChangeLoading(false);
            }
        },
        [dispatch, activeCustomView, customViews, applyDefaultTab, closeViewAction, t]
    );

    const viewModalText = useMemo(
        () =>
            viewAction.rename_view
                ? { title: t('QuotingHub.QuotingDashboard.Rename_View'), primaryBtnText: t('Common.Save') }
                : { title: t('QuotingHub.QuotingDashboard.Create_New_View'), primaryBtnText: t('QuotingHub.QuotingDashboard.Create_View') },
        [viewAction.rename_view, t]
    );

    return {
        tabs,
        customViews,
        viewNames,
        activeViewTab,
        activeCustomView,
        isDefaultTab,
        isCreateViewEnable,
        isSaveEnable,
        getToday,
        isTabsLoading,
        isViewChangeLoading,
        isError,
        setIsError,
        viewAction,
        viewModalText,
        applyView,
        applyDefaultTab,
        revertToBaseline,
        applyModalFilters,
        openCreateView,
        openRenameView,
        openDeleteView,
        closeViewAction,
        saveActiveView,
        createView,
        updateView,
        handleViewModalSave,
        handleDeleteModalClose,
    };
};

export default useTrackViews;
